import { useEffect, useRef } from 'react';

/**
 * The one moment in this product that is allowed to be loud.
 *
 * §6.5 rejects the category's animated coin showers, and this deliberately is
 * not one: no gold, no chrome, no currency. It is paper — the same stock as the
 * cards, in the palette the rest of the app already uses — thrown hard and then
 * left to fall. Winning a session with your friends is the payoff the whole
 * screen exists for, and a screenshot of it should still look like this app.
 *
 * Canvas rather than DOM nodes: two hundred elements animating at once is the
 * difference between a celebration and a stutter. It draws nothing at all under
 * `prefers-reduced-motion`.
 */

export interface CelebrationProps {
  /** Bumping this fires another volley, so "celebrate again" is a state bump. */
  fire: number;
  /** Half the paper, one cannon — for a result worth marking but not yours. */
  restrained?: boolean;
}

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Half-width and half-height in px. */
  w: number;
  h: number;
  /** Spin about the long axis, which is what makes paper flash and vanish. */
  tilt: number;
  tiltSpeed: number;
  angle: number;
  spin: number;
  colour: string;
  drag: number;
  fade: number;
}

const GRAVITY = 0.00075; // px per ms²
const LIFETIME = 5200; // ms before the last piece has faded out

export function Celebration({ fire, restrained = false }: CelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (fire === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Read the palette off the document so dark mode, and any future retheme,
    // come along for free.
    const style = getComputedStyle(document.documentElement);
    const value = (name: string, fallback: string) =>
      style.getPropertyValue(name).trim() || fallback;
    const palette = [
      value('--signal', '#d6452b'),
      value('--oxblood', '#5c1a26'),
      value('--ink', '#141a2e'),
      value('--oat-deep', '#ddd5c5'),
      value('--card-face', '#fdfcf9'),
      value('--signal', '#d6452b'),
    ];

    let width = 0;
    let height = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const pieces: Piece[] = [];
    const make = (x: number, y: number, vx: number, vy: number, fade: number): Piece => ({
      x,
      y,
      vx,
      vy,
      w: 3 + Math.random() * 5,
      h: 4 + Math.random() * 7,
      tilt: Math.random() * Math.PI * 2,
      tiltSpeed: 0.004 + Math.random() * 0.009,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.012,
      colour: palette[Math.floor(Math.random() * palette.length)]!,
      drag: 0.9986,
      fade,
    });

    /** A cannon: a tight cone of paper fired from a bottom corner. */
    const cannon = (fromLeft: boolean, count: number) => {
      const x = fromLeft ? -10 : width + 10;
      const y = height * 0.92;
      for (let i = 0; i < count; i++) {
        const spread = (Math.random() - 0.5) * 0.7;
        const angle = (fromLeft ? -Math.PI * 0.32 : -Math.PI * 0.68) + spread;
        const speed = 1.05 + Math.random() * 0.85;
        pieces.push(
          make(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, 3600 + Math.random() * 1400),
        );
      }
    };

    /** The shower: paper already in the air, drifting down over the panel. */
    const shower = (count: number, delaySpread: number) => {
      for (let i = 0; i < count; i++) {
        const piece = make(
          Math.random() * width,
          -20 - Math.random() * delaySpread,
          (Math.random() - 0.5) * 0.16,
          0.06 + Math.random() * 0.1,
          4200 + Math.random() * 1000,
        );
        pieces.push(piece);
      }
    };

    cannon(true, restrained ? 34 : 70);
    if (!restrained) cannon(false, 70);
    shower(restrained ? 26 : 64, restrained ? 700 : 1600);

    let raf = 0;
    let last = performance.now();
    let elapsed = 0;

    const frame = (now: number) => {
      // A backgrounded tab hands back a vast delta; cap it so nothing teleports.
      const dt = Math.min(now - last, 48);
      last = now;
      elapsed += dt;

      context.clearRect(0, 0, width, height);
      for (const p of pieces) {
        p.vy += GRAVITY * dt;
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.tilt += p.tiltSpeed * dt;
        p.angle += p.spin * dt;
        // Flutter: the horizontal wobble a falling rectangle actually has.
        p.x += Math.sin(p.tilt) * 0.035 * dt;
        if (p.y - 20 > height) continue;

        const alpha = elapsed < p.fade ? 1 : Math.max(0, 1 - (elapsed - p.fade) / 900);
        if (alpha <= 0) continue;

        context.save();
        context.translate(p.x, p.y);
        context.rotate(p.angle);
        // Scaling the height by the tilt is the whole trick: the piece turns
        // edge-on, disappears for a frame, and comes back.
        context.scale(1, Math.cos(p.tilt));
        context.globalAlpha = alpha;
        context.fillStyle = p.colour;
        context.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        context.restore();
      }

      if (elapsed < LIFETIME) {
        raf = requestAnimationFrame(frame);
      } else {
        context.clearRect(0, 0, width, height);
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [fire, restrained]);

  return <canvas ref={canvasRef} className="celebration" aria-hidden="true" />;
}
